import axios from 'axios';
import { applicantV2ApiUrl } from '../config';
import { ApplicationStatus, FitAssessmentAnswers, LocalKnowledgeAnswers } from '../types/wizard';
import { mapV2ApplicationStateToWizardStep } from './wizardSteps';

const SESSION_STORAGE_KEY = 'valleyeats.applicantV2.session';

export interface ApplicantV2Session {
  applicationId: string;
  sessionToken: string;
}

export interface ApplicantV2State {
  applicationId: string;
  applicationStatus: 'IN_PROGRESS' | 'SUBMITTED' | 'NOT_APPROVED' | 'APPROVED';
  currentStep: 'FIT_ASSESSMENT' | 'LOCAL_KNOWLEDGE' | 'VIDEO' | 'REQUIREMENTS' | 'COMPLETE';
  deliveryArea: string;
  fitAssessmentStatus: string;
  agreementStatus: string;
  knowledgeStatus: string;
  videoStatus: string;
  driversLicenseStatus: string;
  insuranceStatus: string;
  reapplyEligibleAt?: string | null;
}

export interface ApplicantMarket {
  value: string;
  label: string;
}

const authConfig = (session: ApplicantV2Session) => ({
  headers: { Authorization: `Bearer ${session.sessionToken}` },
});

export const saveApplicantV2Session = (session: ApplicantV2Session) => {
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
};

export const loadApplicantV2Session = (): ApplicantV2Session | null => {
  try {
    const stored = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) return null;
    const session = JSON.parse(stored);
    return typeof session.applicationId === 'string' && typeof session.sessionToken === 'string'
      ? session
      : null;
  } catch {
    return null;
  }
};

export const clearApplicantV2Session = () => {
  window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
};

export const getApplicantV2ErrorMessage = (error: unknown, fallback: string) => {
  if (!axios.isAxiosError(error)) return fallback;
  const responseError = error.response?.data?.error;
  if (typeof responseError === 'string') return responseError;
  return responseError?.message || fallback;
};

export const getApplicantMarkets = async (): Promise<ApplicantMarket[]> => {
  const response = await axios.get(applicantV2ApiUrl('/markets'));
  return response.data.markets || [];
};

export const createApplicantV2 = async (payload: Record<string, unknown>): Promise<ApplicantV2Session> => {
  const response = await axios.post(applicantV2ApiUrl(''), payload);
  return {
    applicationId: response.data.applicationId,
    sessionToken: response.data.sessionToken,
  };
};

export const requestResumeEmailCode = async (email: string) => {
  const response = await axios.post(applicantV2ApiUrl('/resume/challenge'), { email });
  return response.data;
};

export const verifyResumeEmailCode = async (
  email: string,
  code: string
): Promise<ApplicantV2Session & ApplicantV2State> => {
  const response = await axios.post(applicantV2ApiUrl('/resume/verify'), { email, code });
  return response.data;
};

export const getApplicantV2State = async (session: ApplicantV2Session): Promise<ApplicantV2State> => {
  const response = await axios.get(applicantV2ApiUrl(`/${session.applicationId}`), authConfig(session));
  return response.data;
};

export const toApplicationStatus = (state: ApplicantV2State): ApplicationStatus => ({
  applicationId: state.applicationId,
  applicationStatus: state.applicationStatus,
  backendCurrentStep: state.currentStep,
  currentStepId: mapV2ApplicationStateToWizardStep(state.currentStep, state.applicationStatus),
  applicantStep: state.currentStep,
  deliveryArea: state.deliveryArea,
  email: '',
  name: '',
  documentToken: '',
  steps: [],
  fitAssessmentStatus: state.fitAssessmentStatus,
  agreementStatus: state.agreementStatus,
  knowledgeStatus: state.knowledgeStatus,
  videoStatus: state.videoStatus,
  driversLicenseStatus: state.driversLicenseStatus,
  insuranceStatus: state.insuranceStatus,
  reapplyEligibleAt: state.reapplyEligibleAt,
});

const postForSession = (session: ApplicantV2Session, path: string, body: Record<string, unknown>) =>
  axios.post(applicantV2ApiUrl(`/${session.applicationId}${path}`), body, authConfig(session));

export const submitFitAssessment = (session: ApplicantV2Session, answers: FitAssessmentAnswers) =>
  postForSession(session, '/fit-assessment', { answers });

export const acceptCourierAgreement = (session: ApplicantV2Session) =>
  postForSession(session, '/agreement', { accepted: true, version: 'v1' });

export const submitLocalKnowledge = (session: ApplicantV2Session, answers: LocalKnowledgeAnswers) =>
  postForSession(session, '/local-knowledge', { answers });

export const requestVideoUpload = async (session: ApplicantV2Session, file: Blob) => {
  const response = await postForSession(session, '/video/upload-url', {
    contentType: file.type || 'video/webm',
    fileSize: file.size,
  });
  return response.data as { uploadUrl: string; videoKey: string; expiresIn: number };
};

export const confirmVideoUpload = (session: ApplicantV2Session, videoKey: string, duration: number) =>
  postForSession(session, '/video/confirm', { videoKey, duration });

export const skipVideo = (session: ApplicantV2Session) =>
  postForSession(session, '/video/skip', {});

export const requestDocumentUpload = async (
  session: ApplicantV2Session,
  documentType: string,
  file: File
) => {
  const response = await postForSession(session, '/documents/upload-url', {
    documentType,
    contentType: file.type,
    fileSize: file.size,
  });
  return response.data as { uploadUrl: string; documentKey: string; expiresIn: number };
};

export const confirmDocumentUpload = (
  session: ApplicantV2Session,
  documentType: string,
  documentKey: string
) => postForSession(session, '/documents/confirm', { documentType, documentKey });

export const submitApplicantV2 = (session: ApplicantV2Session) =>
  postForSession(session, '/submit', {});
